import { Scenario } from '@models/scenario';
import { Component, Input, ViewChild, Renderer2, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { SimulationSetService } from '@services/simulation-set/simulation-set.service';
import { ResultsetItem } from '@models/resultset-item';
import { DatasetDetails } from '@models/dataset';
import { SettingsService } from '@services/settings/settings.service';
import { SimulationSetValidationErrorType, SimulationSetValidationError } from '@interfaces/simulation-set-errors';

@Component({
  selector: 'app-baseline-details',
  templateUrl: './baseline-details.component.html',
  styleUrls: ['./baseline-details.component.scss']
})
export class BaselineDetailsComponent implements AfterViewInit {
  @ViewChild('newStressSelector', { static: false }) newStressSelector: ElementRef;
  @Input() baselineGroup: ResultsetItem;
  @Input() index: number;
  private oldDataset: number;
  public changeDatasetVisible = false;
  public isRowVisible = true;
  public duplicateBaselineOnSet = false;
  public duplicateBaselineMessage = '';

  public get datasets$() {
    return this.simService.datasetArray$;
  }

  public get baselines$() {
    return this.simService.baselineList$;
  }

  public get stresses$() {
    return this.simService.stressList$;
  }

  public get validMnemonic(): boolean {
    const invalid = new Set([
      SimulationSetValidationErrorType.DuplicateMnemonic,
      SimulationSetValidationErrorType.InvalidMnemonic,
      SimulationSetValidationErrorType.MnemonicAlreadyInDB
    ]);
    return !this.validationInfo || !invalid.has(this.validationInfo.type);
  }

  public get validDataset(): boolean {
    return !this.validationInfo || this.validationInfo.type !== SimulationSetValidationErrorType.MissingDataset;
  }

  public get validationInfo(): SimulationSetValidationError {
    return this.baselineGroup.ValidationError;
  }

  constructor(
    private simService: SimulationSetService,
    private renderer: Renderer2,
    private settingsService: SettingsService,
    private cd: ChangeDetectorRef
  ) {}

  ngAfterViewInit() {
    this.cd.detectChanges();
    this.oldDataset = this.baselineGroup.DataSetID;
  }

  public getScenarioColor() {
    return this.settingsService.getScenarioColor(this.baselineGroup);
  }

  public removeGroup() {
    this.simService.removeBaselineFromState(this.baselineGroup.id);
  }

  public onScenarioSelect = event => {
    if (event.type === 'change') {
      const selectElementValue = Number(event.target.value);
      const scenarioDetails: Scenario = this.simService.baselineList.get(selectElementValue);
      const mayexist = this.simService.findResultsetItemInDB(scenarioDetails.scenario_id, this.baselineGroup.DataSetID);

      this.baselineGroup.RunID = this.simService.getNewUUID();
      if (mayexist) {
        this.baselineGroup.Mnemonic = mayexist.Mnemonic;
        this.baselineGroup.Description = mayexist.Description;
        if (mayexist.RunID) {
          this.baselineGroup.RunID = mayexist.RunID;
        }
        if (mayexist.ResultSetID) {
          this.baselineGroup.ResultSetID = mayexist.ResultSetID;
        }
      } else {
        this.baselineGroup.Mnemonic = scenarioDetails.mnemonic;
        this.baselineGroup.Description = scenarioDetails.scenario_description;
        this.baselineGroup.ResultSetID = -1;
      }
      this.baselineGroup.Scenario_ID = scenarioDetails.scenario_id;
      this.baselineGroup.Scenario_Mnemonic = scenarioDetails.mnemonic;
      this.simService.cascadeScenarioMnemonicChangeFromBaseline(this.baselineGroup);

      this.onChange();
    }
  };

  public combinationAlreadyExits() {
    const arrayWithScenariosAndDatasets = this.simService.simulationSetState.map(x => ({
      scenario: x.Scenario_ID,
      dataset: x.DataSetID
    }));
    const found = arrayWithScenariosAndDatasets.filter(
      item => item.dataset === this.baselineGroup.DataSetID && item.scenario === this.baselineGroup.Scenario_ID
    );
    return found && found.length > 1;
  }

  public changeDatasetToggle() {
    this.changeDatasetVisible = !this.changeDatasetVisible;
  }

  public onDatasetSelect = event => {
    const selectElementValue = Number(event.target.value);
    const datasetDetails: DatasetDetails = this.simService.datasetMap.get(selectElementValue);
    const mayexist = this.simService.findResultsetItemInDB(this.baselineGroup.Scenario_ID, datasetDetails.DataSetID);

    this.baselineGroup.RunID = this.simService.getNewUUID();
    if (datasetDetails) {
      if (mayexist) {
        this.baselineGroup.Mnemonic = mayexist.Mnemonic;
        if (mayexist.RunID) {
          this.baselineGroup.RunID = mayexist.RunID;
        }
        if (mayexist.ResultSetID) {
          this.baselineGroup.ResultSetID = mayexist.ResultSetID;
        }
      } else {
        this.baselineGroup.ResultSetID = -1;
        this.baselineGroup.Mnemonic = this.baselineGroup.Scenario_Mnemonic;
      }

      this.baselineGroup.DataSetID = datasetDetails.DataSetID;
      this.baselineGroup.DataSetName = datasetDetails.DataSetName;
      this.simService.cascadeDatasetChangeFromBaseline(this.baselineGroup, this.oldDataset);
      this.oldDataset = this.baselineGroup.DataSetID;
      this.onChange();
    }
    this.changeDatasetToggle();
  };

  public handleAddNewStress = event => {
    const scenarioId = Number(event.target.value);
    const group = this.baselineGroup;
    this.simService.addNewStressToBaseline(scenarioId, group);
    this.renderer.setProperty(this.newStressSelector.nativeElement, 'selected', 'true');
  };

  public handleClickIfOneItem(event: Event, callback) {
    if (event.target['clicked']) {
      event.target['clicked'] = undefined;
      callback(event);
    } else {
      event.target['clicked'] = true;
    }
  }

  public onChange() {
    this.simService.validateSimulationSet();
  }

  public toggleRow() {
    this.isRowVisible = !this.isRowVisible;
  }
}
